import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { format } from "date-fns";
import { CiCirclePlus, CiCircleMinus } from "react-icons/ci";
import "./CreateInvoicePage.css"; // reuse invoice styles

const CreateEstimatePage = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [estimateType, setEstimateType] = useState("one-time");
  const [estimateDate, setEstimateDate] = useState("");
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const vendorRes = await api.get(`/business/${businessId}/vendors`);
        setVendors(vendorRes.data);

        const productRes = await api.get(`/business/${businessId}/products`);
        setProducts(productRes.data);

        const businessRes = await api.get(`/businesses/${businessId}`);
        if (businessRes.data.sales_tax_rate) {
          setTaxRate(parseFloat(businessRes.data.sales_tax_rate));
        }
      } catch (err) {
        console.error("Error fetching setup data:", err);
      }
    })();
  }, [businessId]);

  const handleAddEntry = () => {
    setEntries(prev => [
      ...prev,
      {
        id: Date.now(),
        date: "",
        productId: "",
        manual: false,
        description: "",
        price: "",
        quantity: "",
        amount: "",
        amountManuallySet: false,
      },
    ]);
  };

  const handleEntryChange = (idx, field, value) => {
    const list = [...entries];
    const entry = list[idx];
    entry[field] = value;

    const isManual = entry.manual;

    if (field === "manual") {
   entry.price = "";
   entry.quantity = "";
   entry.amount = "";
   entry.amountManuallySet = false;
 }

    if (field === "amount") {
      entry.amount = value;
      entry.amountManuallySet = true;
    }
    if (field === "manual" || field === "productId") {
      entry.amountManuallySet = false;
    }

    if (isManual && (field === "price" || field === "quantity")) {
      const price = parseFloat(entry.price || 0);
      const qty = parseFloat(entry.quantity || 0);
      if (!isNaN(price) && !isNaN(qty) && !entry.amountManuallySet) {
        entry.amount = (price * qty).toFixed(2);
      }
    }

    if (!isManual && (field === "productId" || field === "quantity")) {
      const product = products.find(p => p.id === Number(entry.productId));
      const qty = parseFloat(entry.quantity || 0);
      if (product && qty) {
        entry.price = product.price;
        entry.amount = (product.price * qty).toFixed(2);
        entry.amountManuallySet = false;
      }
    }

    setEntries(list);
  };

  const handleSubmit = async () => {
    if (!selectedVendor) {
      alert("Customer is required.");
      return;
    }
    if (estimateType === "one-time" && !estimateDate) {
      alert("Date is required for one-time estimates.");
      return;
    }
    if (entries.length === 0) {
      alert("At least one entry is required.");
      return;
    }

    const processed = entries.map(e => {
      const isManual = e.manual;
      const qty = parseFloat(e.quantity || 1);
      const price = parseFloat(
        isManual
          ? e.price
          : (() => {
              const prod = products.find(p => p.id === Number(e.productId));
              return prod ? prod.price : 0;
            })()
      );
      const amount = parseFloat(e.amount || 0);

      let salesTax = 0;
      if (!isManual) {
        const prod = products.find(p => p.id === Number(e.productId));
        const base = price * (qty || 0);
        if (prod?.taxable && taxRate) {
          salesTax = parseFloat(((base * taxRate) / 100).toFixed(2));
        }
      }

      const prod = products.find(p => p.id === Number(e.productId));
      return {
        entry_date: estimateType === "open" && e.date ? format(new Date(e.date), "yyyy-MM-dd") : null,
        product_id: isManual ? null : e.productId,
        is_manual: isManual,
        description: isManual ? e.description : prod?.item || null,
        type: "expense", // enforce expense-only
        quantity: isNaN(qty) ? null : qty,
        price: isNaN(price) ? null : price,
        amount: isNaN(amount) ? 0 : amount,
        sales_tax: salesTax,
      };
    });

    try {
      // uses your *estimate* create endpoint that sets is_estimate=1
      await api.post(`/business/${businessId}/estimates`, {
        vendor_id: selectedVendor,
        invoice_type: estimateType,
        invoice_date: estimateType === "one-time" ? estimateDate : null,
        entries: processed,
      });
      alert("Estimate created.");
      navigate(`/dashboard/business/${businessId}/invoiceest`);
    } catch (err) {
      console.error("Failed to submit estimate:", err);
      alert("Error submitting estimate.");
    }
  };

  return (
    <div className="create-invoice-container">
      <h2>Create New Estimate</h2>

      <div className="invoice-top-row">
        <select value={selectedVendor} onChange={e => setSelectedVendor(e.target.value)}>
          <option value="">Select Customer</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>

        <select value={estimateType} onChange={e => setEstimateType(e.target.value)}>
          <option value="one-time">One Time</option>
          <option value="open">Open</option>
        </select>

        {estimateType === "one-time" && (
          <label className="first-date">
            <p>Date</p>
            <input type="date" value={estimateDate} onChange={e => setEstimateDate(e.target.value)} />
          </label>
        )}
      </div>

      {entries.map((entry, idx) => (
        <div className="invoice-entry-box" key={entry.id}>

  {/* ✅ exact same structure as invoice page */}
  <div className="entry-type-wrapper">
    <div className="entry-mode-switch">
      <button
        type="button"
        className={entry.manual ? '' : 'active'}
        onClick={() => handleEntryChange(idx, "manual", false)}
      >
        Product
      </button>
      <button
        type="button"
        className={entry.manual ? 'active' : ''}
        onClick={() => handleEntryChange(idx, "manual", true)}
      >
        Manual
      </button>
    </div>
  </div>

  {/* ✅ same container + input class for date (only for "open") */}
  {estimateType === "open" && (
    <div className="invoice-date-entry">
      <label>Date</label>
      <input
        type="date"
        className="entry-date-input"
        value={entry.date || ""}
        onChange={(e) => handleEntryChange(idx, "date", e.target.value)}
      />
    </div>
  )}

  {entry.manual ? (
    <>
      <input
        type="text"
        placeholder="Description"
        value={entry.description}
        onChange={(e) => handleEntryChange(idx, "description", e.target.value)}
      />
      {/* ❌ no Type select on estimates (expense-only) */}
      <input
        className="small-input"            
        type="number"
        placeholder="Price"
        value={entry.price}
        onChange={(e) => handleEntryChange(idx, "price", e.target.value)}
      />
      <input
        className="small-input"             
        type="number"
        placeholder="Qty"
        value={entry.quantity}
        onChange={(e) => handleEntryChange(idx, "quantity", e.target.value)}
      />
      <input
        type="number"
        placeholder="Amount"
        value={entry.amount}
        onChange={(e) => handleEntryChange(idx, "amount", e.target.value)}
      />
    </>
  ) : (
    <>
      <select
        value={entry.productId}
        onChange={(e) => handleEntryChange(idx, "productId", e.target.value)}
      >
        <option value="">Select Product</option>
        {products.map(p => (
          <option key={p.id} value={p.id}>{p.item}</option>
        ))}
      </select>
      <input
        type="number"
        placeholder="Qty"
        value={entry.quantity}
        onChange={(e) => handleEntryChange(idx, "quantity", e.target.value)}
      />
      <input
        type="number"
        placeholder="Amount"
        value={entry.amount}
        readOnly
      />
    </>
  )}
</div>
      ))}

      <div className="entry-control-row">
        <button onClick={handleAddEntry}><CiCirclePlus /> Add Entry</button>
        {entries.length > 0 && (
          <button onClick={() => setEntries(prev => prev.slice(0, -1))}><CiCircleMinus /> Remove Entry</button>
        )}
      </div>

      <div className="submit-control-row">
        <button className="submit-invoice" onClick={handleSubmit}>Submit Estimate</button>
        <button className="cancel-invoice" onClick={() => navigate(-1)}>Cancel</button>
      </div>
    </div>
  );
};

export default CreateEstimatePage;
